<template>
  <!-- Phase 6: polish parity with payments (empty/header/card/details/more). -->
  <div class="blm" :class="{ 'blm--selecting': selectionMode }" dir="rtl">
    <div class="blm__header">
      <div class="blm__bar">
        <div class="blm__bar-actions">
          <button
            type="button"
            class="blm__bar-btn blm__bar-btn--create"
            :aria-label="$t(bankList.type)"
            @click="goToNew"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="blm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="blm__title">{{ $t($route.name) }}</h1>
        <button
          type="button"
          class="blm__bar-btn blm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="blm__search-row">
        <div class="blm__search">
          <v-icon size="20" color="#90A4AE" class="blm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="bankList.search"
            type="search"
            class="blm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="bankList.search"
            type="button"
            class="blm__search-clear"
            :aria-label="$t('clear')"
            @click="bankList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="blm__filter-btn"
          :class="{ 'blm__filter-btn--active': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon
            size="22"
            :color="hasAnyFilter ? '#1565C0' : '#546E7A'"
          >
            mdi-filter-outline
          </v-icon>
        </button>
        <button
          type="button"
          class="blm__filter-btn"
          :class="{ 'blm__filter-btn--active': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="blm__select-bar" dir="rtl">
        <button
          type="button"
          class="blm__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="blm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="blm__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div v-if="bankList.loading && !displayItems.length" class="blm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="blm__state blm__state--empty">
      <img
        class="blm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="blm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="blm__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="blm-card"
        :class="{
          'blm-card--selected': selectionMode && isSelected(item),
        }"
      >
        <div
          v-if="selectionMode"
          class="blm-card__select-overlay"
          :class="{ 'blm-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="blm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="blm-card__head">
          <div class="blm-card__title-block">
            <button
              type="button"
              class="blm-card__name-link"
              @click="bankList.edit(item)"
            >
              <span class="blm-card__name">{{ item.name || "—" }}</span>
              <v-icon size="12" color="#879EB6">mdi-pencil-outline</v-icon>
            </button>
            <div class="blm-card__sub">
              <template v-if="isPetty">
                {{ $t("accountCode") }}: {{ item.fin_id || "—" }}
              </template>
              <template v-else>
                {{ $t("number") }}: {{ item.number || "—" }}
              </template>
            </div>
          </div>
          <span class="blm-card__number">#{{ item.id }}</span>
        </div>

        <div class="blm-card__amounts">
          <div class="blm-card__amount-row">
            <span class="blm-card__amount-label">{{ $t("remain") }}</span>
            <span class="blm-card__amount-value blm-card__amount-value--remain">
              {{ remainText(item) }}
            </span>
          </div>
          <div class="blm-card__amount-row">
            <span class="blm-card__amount-label">{{ $t("currency") }}</span>
            <span class="blm-card__amount-value">
              {{ item.currency && item.currency.name ? item.currency.name : "—" }}
            </span>
          </div>
        </div>

        <div v-if="!selectionMode" class="blm-card__footer">
          <div class="blm-card__actions">
            <button
              v-show="removePermission(bankList.type)"
              type="button"
              class="blm-btn blm-btn--danger"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              type="button"
              class="blm-btn blm-btn--primary"
              @click="bankList.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="blm-btn blm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <span class="blm-card__status">{{ statusText(item) }}</span>
        </div>
      </article>
      <div v-if="appending" class="blm__state blm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="blm__sentinel" />

    <div v-if="selectionMode" class="blm-bulkbar" dir="rtl">
      <button
        v-if="removePermission(bankList.type)"
        type="button"
        class="blm-bulkbar__btn blm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="blm-sheet">
        <div class="blm-sheet__handle" />
        <div class="blm-sheet__header" dir="rtl">
          <div class="blm-sheet__header-side">
            <span v-if="detailItem" class="blm-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="blm-sheet__title">{{ $t("details") }}</div>
          <div class="blm-sheet__header-side blm-sheet__header-side--end">
            <button
              type="button"
              class="blm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="detailItem" class="blm-sheet__quickbar" dir="rtl">
          <button
            type="button"
            class="blm-sheet__quick"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            type="button"
            class="blm-sheet__quick"
            v-clipboard:copy="copyLink(detailItem)"
            @click="onCopyLinkQuick"
          >
            <v-icon size="18" color="#5B7C99">mdi-link</v-icon>
            {{ $t("copy-link") }}
          </button>
          <button
            type="button"
            class="blm-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="blm-sheet__name-block" dir="rtl">
          <div class="blm-sheet__name-row">
            <span class="blm-sheet__row-label">
              {{ $t("name") }} {{ $t(bankList.type) }}
            </span>
            <span class="blm-sheet__name-value">
              {{ detailItem.name || "—" }}
            </span>
          </div>
          <button
            v-if="detailItem.fin_id"
            type="button"
            class="blm-sheet__linkrow"
            @click="openTurnover(detailItem)"
          >
            <v-icon size="18" color="#00796B">mdi-playlist-check</v-icon>
            <span class="blm-sheet__linkrow-text">{{ $t("turnover") }}</span>
            <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="blm-sheet__body" dir="rtl">
          <div class="blm-sheet__card">
            <!-- Desktop order: after name → pettyPerson (petty) → main cols → tags → meta -->
            <div v-if="isPetty" class="blm-sheet__row">
              <span class="blm-sheet__row-label">{{ $t("pettyPerson") }}</span>
              <span
                v-if="
                  detailItem.user_financial && detailItem.user_financial.length
                "
                class="blm-sheet__tags"
              >
                <v-chip
                  v-for="(user, ui) in detailItem.user_financial"
                  :key="'detail-uf-' + ui"
                  color="success"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">
                    {{ user && user.user ? user.user.name : "—" }}
                  </span>
                </v-chip>
              </span>
              <span v-else class="blm-sheet__row-value">—</span>
            </div>

            <div
              v-for="(row, i) in detailMainRows(detailItem)"
              :key="'detail-main-' + i"
              class="blm-sheet__row"
              :class="{
                'blm-sheet__row--band':
                  row.kind === 'band' ||
                  row.kind === 'amount' ||
                  row.kind === 'money',
                'blm-sheet__row--remain': row.kind === 'amount',
              }"
            >
              <span class="blm-sheet__row-label">{{ row.label }}</span>
              <span class="blm-sheet__row-value">{{ row.value }}</span>
            </div>

            <div class="blm-sheet__row">
              <span class="blm-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="blm-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span v-else class="blm-sheet__row-value">—</span>
            </div>

            <div
              v-for="(row, i) in detailMetaRows(detailItem)"
              :key="'detail-meta-' + i"
              class="blm-sheet__row"
            >
              <span class="blm-sheet__row-label">{{ row.label }}</span>
              <span class="blm-sheet__row-value">{{ row.value }}</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="moreSheet">
      <v-card class="blm-sheet">
        <div class="blm-sheet__handle" />
        <div class="blm-sheet__header" dir="rtl">
          <span class="blm-sheet__header-spacer" />
          <div class="blm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="blm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="blm-sheet__body" dir="rtl">
          <div class="blm-sheet__actions blm-sheet__actions--sheet">
            <button
              v-if="detailItem.pos"
              type="button"
              class="blm-sheet__action"
              v-clipboard:copy="posPaymentLink(detailItem)"
              @click="onCopyPosQuick"
            >
              <v-icon size="20" color="#5B7C99">
                mdi-account-credit-card-outline
              </v-icon>
              <span>{{ $t("posPaymentPage") }}</span>
            </button>
            <div class="blm-sheet__action blm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="logType"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter: same shell fields as desktop (status + tag). -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="blm-sheet blm-sheet--filter">
        <div class="blm-sheet__handle" />
        <div class="blm-sheet__header" dir="rtl">
          <span class="blm-sheet__header-spacer" />
          <div class="blm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="blm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="blm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="blm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="blm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="blm-filter-nav__texts">
              <span class="blm-filter-nav__item-title">{{ group.title }}</span>
              <span class="blm-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="blm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="blm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="blm-sheet__btn blm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="blm-sheet__btn blm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="blm-sheet blm-sheet--filter">
        <div class="blm-sheet__handle" />
        <div class="blm-sheet__header" dir="rtl">
          <span class="blm-sheet__header-spacer" />
          <div class="blm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="blm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="blm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'status'">
            <div class="blm-f-field-wrap">
              <div class="blm-f-field-wrap__label">{{ $t("status") }}</div>
              <button
                type="button"
                class="blm-f-picker-field"
                @click="
                  openSelectLayer(
                    'productStatus',
                    $t('status'),
                    bankList.productStatusItems || [],
                    'text',
                    true
                  )
                "
              >
                <span class="blm-f-picker-field__text">
                  {{
                    selectSummary(
                      "productStatus",
                      bankList.productStatusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="blm-f-field-wrap">
              <div class="blm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="blm-f-picker-field"
                @click="openPicker('bmTag')"
              >
                <span class="blm-f-picker-field__text">
                  {{ pickerSummary("bmTag", bankList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="blm-picker-host" aria-hidden="true">
                <tag ref="bmTag" v-model="bankList.tag" />
              </div>
            </div>
          </template>
        </div>

        <div class="blm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="blm-sheet__btn blm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="blm-sheet__btn blm-sheet__btn--ghost"
            @click="backToFilterMain"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="blm-sheet blm-sheet--filter">
        <div class="blm-sheet__handle" />
        <div class="blm-sheet__header" dir="rtl">
          <span class="blm-sheet__header-spacer" />
          <div class="blm-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="blm-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="blm-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="blm-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="blm-select-layer__text">
              {{ item[selectLayerTextKey] }}
            </span>
            <v-icon
              v-if="isSelectLayerSelected(item.value)"
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="
              selectLayerField && hasValue(bankList[selectLayerField])
            "
            type="button"
            class="blm-select-layer__clear"
            @click="selectLayerClear"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="blm-confirm-sheet" dir="rtl">
        <div class="blm-confirm-sheet__handle" />
        <div class="blm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="blm-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="blm-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="blm-confirm-sheet__rows">
          <div class="blm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="blm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="blm-confirm-sheet__item"
            >
              <span class="blm-confirm-sheet__item-main">
                <span class="blm-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="blm-confirm-sheet__item-name">
                  {{ sel.name || "—" }}
                </span>
              </span>
              <span class="blm-confirm-sheet__item-number">#{{ sel.id }}</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="bankList.deleteItem"
          class="blm-confirm-sheet__rows"
        >
          <div class="blm-confirm-sheet__row">
            <span>{{ $t("name") }} {{ $t(bankList.type) }}</span>
            <strong>{{ bankList.deleteItem.name || "—" }}</strong>
          </div>
          <div v-if="!isPetty" class="blm-confirm-sheet__row">
            <span>{{ $t("number") }}</span>
            <strong>{{ bankList.deleteItem.number || "—" }}</strong>
          </div>
          <div v-else class="blm-confirm-sheet__row">
            <span>{{ $t("accountCode") }}</span>
            <strong>{{ bankList.deleteItem.fin_id || "—" }}</strong>
          </div>
          <div class="blm-confirm-sheet__row">
            <span>{{ $t("id") }}</span>
            <strong>{{ bankList.deleteItem.id }}</strong>
          </div>
          <div class="blm-confirm-sheet__row">
            <span>{{ $t("remain") }}</span>
            <strong>{{ remainText(bankList.deleteItem) }}</strong>
          </div>
          <div class="blm-confirm-sheet__row">
            <span>{{ $t("currency") }}</span>
            <strong>
              {{
                bankList.deleteItem.currency &&
                bankList.deleteItem.currency.name
                  ? bankList.deleteItem.currency.name
                  : "—"
              }}
            </strong>
          </div>
        </div>
        <div class="blm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="blm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="blm-confirm-sheet__no"
            @click="cancelDelete"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "BankListMobile",
  inject: ["bankList"],
  components: {
    Tag: () => import("../../../../components/general/Tag/tag"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      selectLayerMulti: false,
      selectionMode: false,
      selectedItems: [],
      _selectionSynced: false,
      deleteSheet: false,
      deleteGroupMode: false,
    };
  },
  computed: {
    currentPage() {
      const opts = this.bankList.tableOptions || {};
      return opts.page || 1;
    },
    isPetty() {
      return this.bankList.type === "petty";
    },
    logType() {
      return this.bankList.typeForm || this.bankList.type;
    },
    hasAnyFilter() {
      return this.groupHasValue("status") || this.groupHasValue("category");
    },
    filterGroups() {
      return [
        {
          key: "status",
          icon: "mdi-list-status",
          title: this.$t("status"),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("tag"),
        },
      ];
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
  },
  watch: {
    "bankList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "bankList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.bankList.tableOptions = Object.assign(
          {},
          this.bankList.tableOptions,
          { page: 1 }
        );
      }
    },
    "bankList.selected"(val) {
      if (!this.selectionMode || !this._selectionSynced) return;
      if (!val || !val.length) {
        this._selectionSynced = false;
        this.exitSelectionMode();
      }
    },
    deleteSheet(val) {
      if (!val) this.deleteGroupMode = false;
    },
  },
  mounted() {
    this.displayItems = (this.bankList.items || []).slice();
    this.bankList.tableOptions = Object.assign({}, this.bankList.tableOptions, {
      page: 1,
      itemsPerPage: 10,
    });
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    goToNew() {
      if (typeof this.bankList.goTo === "function") {
        this.bankList.goTo(this.bankList.type);
      }
    },
    onRefreshTap() {
      this.appending = false;
      this.bankList.tableOptions = Object.assign(
        {},
        this.bankList.tableOptions,
        { page: 1 }
      );
      this.bankList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      this.filterSheet = true;
    },
    openPicker(refName) {
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openSelectLayer(field, title, items, textKey, multi) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerMulti = !!multi;
      this.selectLayerOpen = true;
    },
    isSelectLayerSelected(value) {
      if (!this.selectLayerField) return false;
      const current = this.bankList[this.selectLayerField];
      if (this.selectLayerMulti) {
        return Array.isArray(current) && current.indexOf(value) !== -1;
      }
      return current === value;
    },
    selectLayerPick(value) {
      if (!this.selectLayerField) return;
      if (this.selectLayerMulti) {
        const current = Array.isArray(this.bankList[this.selectLayerField])
          ? this.bankList[this.selectLayerField].slice()
          : [];
        const idx = current.indexOf(value);
        if (idx === -1) current.push(value);
        else current.splice(idx, 1);
        this.bankList[this.selectLayerField] = current.length ? current : null;
        return;
      }
      this.bankList[this.selectLayerField] = value;
      this.selectLayerOpen = false;
    },
    selectLayerClear() {
      if (this.selectLayerField) {
        this.bankList[this.selectLayerField] = null;
      }
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.bankList[field];
      if (!this.hasValue(v)) return "—";
      const key = textKey || "text";
      if (Array.isArray(v)) {
        const names = [];
        v.forEach((val) => {
          const found = (items || []).find((i) => i && i.value === val);
          if (found) names.push(found[key]);
        });
        return names.length ? names.join(" · ") : String(v.length);
      }
      const found = (items || []).find((i) => i && i.value === v);
      return found ? found[key] : "—";
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && (i.id === v || i.value === v));
          if (found && (found.name || found.text)) {
            names.push(found.name || found.text);
          }
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    backToFilterMain() {
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.bankList;
      if (key === "status") return this.hasValue(list.productStatus);
      if (key === "category") return this.hasValue(list.tag);
      return false;
    },
    groupSubtitle(key) {
      if (key === "status") {
        return this.selectSummary(
          "productStatus",
          this.bankList.productStatusItems || [],
          "text"
        );
      }
      if (key === "category") {
        return this.hasValue(this.bankList.tag)
          ? this.pickerSummary("bmTag", this.bankList.tag)
          : this.$t("tag");
      }
      return "";
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.selectLayerOpen = false;
      this.appending = false;
      this.bankList.tableOptions = Object.assign(
        {},
        this.bankList.tableOptions,
        { page: 1 }
      );
      this.bankList.getItems();
    },
    clearFilter() {
      const list = this.bankList;
      list.productStatus = null;
      list.tag = null;
      this.applyFilter();
    },
    removePermission(type) {
      if (typeof this.bankList.removePermission === "function") {
        return this.bankList.removePermission(type);
      }
      return false;
    },
    remainText(item) {
      if (!item || !item.remain) {
        return this.$numberFormat ? this.$numberFormat(0) : "0";
      }
      let text = this.$numberFormat
        ? this.$numberFormat(item.remain.remain)
        : item.remain.remain;
      const storeCur = this.$store.state.currency;
      if (
        item.remain.currency &&
        storeCur &&
        storeCur.id !== item.remain.currency.id
      ) {
        const alt = this.$numberFormat
          ? this.$numberFormat(item.remain.currency_remain)
          : item.remain.currency_remain;
        text +=
          " (" + alt + "  " + (item.remain.currency.symbol || "") + ")";
      }
      return text;
    },
    statusText(item) {
      if (!item) return "—";
      const parts = [];
      if (item.status === "active") parts.push(this.$t("active"));
      if (item.lead === "active") parts.push(this.$t("lead"));
      return parts.length ? parts.join(" / ") : "—";
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    selectedRowNumber(item) {
      if (!item) return "";
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.bankList.selected = [];
      this._selectionSynced = false;
      this.bankList.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.bankList.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    cancelDelete() {
      this.deleteSheet = false;
      this.deleteGroupMode = false;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.bankList.selected && this.bankList.selected.length > 0) {
        this.bankList.removegroup();
      } else {
        this.bankList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this._selectionSynced = false;
        this.bankList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this._selectionSynced = false;
      this.bankList.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (i) => i.id !== item.id
        );
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.bankList.selected = this.selectedItems.slice();
      this._selectionSynced = true;
    },
    copyLink(item) {
      if (!item) return "";
      if (typeof this.bankList.editLink === "function") {
        return this.bankList.editLink(item);
      }
      const t = (item && item.type) || this.bankList.type || "bank";
      return this.$baseURL + "/" + t + "/edit/" + item.id;
    },
    posPaymentLink(item) {
      if (!item) return "";
      return this.$baseURL + "/cashPayment/invoice/" + item.id;
    },
    notifyCopied() {
      if (typeof this.bankList.copied === "function") {
        this.bankList.copied();
      }
    },
    onCopyLinkQuick() {
      this.notifyCopied();
    },
    onCopyPosQuick() {
      this.notifyCopied();
      this.moreSheet = false;
    },
    openTurnover(item) {
      if (!item || !item.fin_id) return;
      this.detailSheet = false;
      this.moreSheet = false;
      if (typeof this.bankList.openComponent === "function") {
        this.bankList.openComponent("accountingTurnover", item.fin_id);
      }
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      this.moreSheet = false;
      this.detailSheet = false;
      if (name === "edit") this.bankList.edit(item);
    },
    detailMainRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value, kind) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
          kind: kind || null,
        });
      // Through status (name/petty/tags/meta handled in template).
      push(this.$t("id"), item.id, "band");
      push(this.$t("accountCode"), item.fin_id, "band");
      if (!this.isPetty) {
        push(this.$t("number"), item.number);
      }
      push(
        this.$t("currency"),
        item.currency && item.currency.name
      );
      push(this.$t("remain"), this.remainText(item), "amount");
      push(this.$t("status"), this.statusText(item));
      return rows;
    },
    detailMetaRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
        });
      push(this.$t("branch-name"), item.branch);
      push(this.$t("Account opening date"), item.date);
      push(this.$t("content"), item.content);
      if (item.user) {
        push(
          this.$t("registerar"),
          item.user.name ||
            (
              (item.user.firstname || "") +
              " " +
              (item.user.lastname || "")
            ).trim()
        );
      } else {
        push(this.$t("registerar"), null);
      }
      return rows;
    },
    loadMore() {
      if (!this.displayItems.length) return;
      if (this.bankList.loading || this.appending) return;
      if (this.currentPage >= (this.bankList.pageCount || 1)) return;
      this.appending = true;
      this.bankList.tableOptions = Object.assign(
        {},
        this.bankList.tableOptions,
        { page: this.currentPage + 1 }
      );
    },
  },
};
</script>

<style scoped>
.blm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.blm--selecting .blm__list {
  padding-bottom: 88px;
}
.blm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.blm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.blm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.blm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.blm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.blm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.blm__bar-btn--back {
  margin-inline-start: auto;
}
.blm__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.blm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.blm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
}
.blm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #37474f;
}
.blm__search-clear {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
}
.blm__filter-btn {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 1px solid #e0e4e8;
}
.blm__filter-btn:active {
  background: rgba(91, 124, 153, 0.08);
}
.blm__filter-btn--active {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.35);
}
.blm__select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.blm__select-bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.12);
}
.blm__select-count {
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.blm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.blm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.blm__state--more {
  padding: 14px 0;
}
.blm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.blm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.blm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.blm__sentinel {
  height: 1px;
}
.blm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.blm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.blm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.blm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.blm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.blm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.blm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.blm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.blm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.blm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.blm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.blm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.blm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 10px;
  padding: 2px 8px;
}
.blm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.blm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.blm-card__amount-label {
  font-size: 12px;
  color: #78909c;
}
.blm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  padding: 2px 8px;
  border-radius: 8px;
}
.blm-card__amount-value--remain {
  background: rgba(91, 124, 153, 0.1);
  color: #37474f;
}
.blm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.blm-card__actions {
  display: flex;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
}
.blm-card__status {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  color: #1565c0;
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(21, 101, 192, 0.08);
  white-space: nowrap;
}
.blm-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.blm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.blm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.blm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.blm-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.blm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.blm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.blm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.blm-sheet__header-side--end {
  justify-content: flex-end;
}
.blm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.blm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.blm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.blm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.blm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.blm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.blm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.blm-sheet__quick .v-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
.blm-sheet__quick:active {
  background: rgba(91, 124, 153, 0.14);
}
.blm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.blm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.blm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.blm-sheet__name-block .blm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.blm-sheet__name-block .blm-sheet__linkrow:active {
  background: rgba(255, 255, 255, 0.95);
}
.blm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.blm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
}
.blm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.blm-sheet__card .blm-sheet__row:last-child {
  border-bottom: 0;
}
.blm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.blm-sheet__row--remain {
  background: rgba(91, 124, 153, 0.1);
  border-bottom-color: rgba(91, 124, 153, 0.18);
}
.blm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.blm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.blm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.blm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm-sheet__linkrow:active {
  background: rgba(0, 150, 136, 0.15);
}
.blm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.blm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.blm-sheet__actions--sheet {
  padding-top: 4px;
}
.blm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.blm-sheet__action:active {
  background: rgba(91, 124, 153, 0.08);
}
.blm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.blm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.blm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.blm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.blm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.blm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.blm-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.blm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.blm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.blm-sheet__btn--primary:active {
  background: #4d6a84;
}
.blm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.blm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.blm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.blm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.blm-filter-nav__item:active {
  background: #f7f9fc;
}
.blm-filter-nav__item:last-child {
  border-bottom: 0;
}
.blm-filter-nav__icon {
  flex: 0 0 auto;
}
.blm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.blm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.blm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.blm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.blm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.blm-filter-fields > * {
  margin-bottom: 14px;
}
.blm-picker-host {
  display: none !important;
}
.blm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.blm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.blm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.blm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.blm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.blm-select-layer {
  padding: 6px 16px 20px;
}
.blm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.blm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.blm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.blm-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.blm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.blm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.blm-confirm-sheet__title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
  margin: 0 0 8px;
}
.blm-confirm-sheet__subtitle {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.blm-confirm-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.blm-confirm-sheet__items {
  max-height: 168px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
}
.blm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #e8edf2;
}
.blm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.blm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.blm-confirm-sheet__item-index {
  flex: 0 0 auto;
  min-width: 16px;
  font-size: 11px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.blm-confirm-sheet__item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  color: #37474f;
}
.blm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #5b7c99;
}
.blm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.blm-confirm-sheet__row strong {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.blm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.blm-confirm-sheet__yes {
  border-radius: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.blm-confirm-sheet__no {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
</style>
